import CustomPagination from "components/general/CustomPagination";
import SearchAndFilterBar from "components/general/SearchAndFilterBar";
import CustomSelect from "components/general/CustomSelect";
import { getPaginatedSlice } from "helpers/pagination";
import { ISelectableOption } from "interfaces/ISelectableOption";
import { ISizeRecord } from "interfaces/ISize";
import { useEffect, useState } from "react";
import { Button, Col, Row, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { SizesTable } from "./sizesTable/sizesTable";
import { useAuthStore } from "../../zustand/loginStore";
import { createAuthenticatedAxiosInstance, fetchSizes } from "api/auth";
import { RequestStatesTypes } from "types/RequestsStatesTypes";
import { sortByKey } from "helpers/sorting";
import { IClickedHeader } from "interfaces/ITable";
import { AdminRouteContainer } from "components/general/AdminRouteContainer";
import { isSuccessStatusCode } from "helpers/httpResponse";

/**
 * The Add size main component
 */
export const SizeList = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [sizes, setSizes] = useState<ISizeRecord[]>([]);
  const [filteredSizes, setFilteredSizes] = useState<ISizeRecord[]>(sizes);
  const [foundData, setFoundData] = useState<ISizeRecord[]>(sizes);
  const [paginatedData, setPaginatedData] = useState<ISizeRecord[]>([]);
  const [clickedHeader, setClickedHeader] = useState<IClickedHeader>({
    name: "",
    asc: false,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [requestState, setRequestState] = useState<RequestStatesTypes>("none");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const filterOptions: ISelectableOption[] = [
    { value: "Activados", label: "Activados" },
    { value: "Desactivados", label: "Desactivados" },
    { value: "Todos", label: "Todos" },
  ];
  const [filter, setFilter] = useState<ISelectableOption | null>(null);

  /**
   * Per page options for pagination
   * @constant {Array<{value: string, label: string}>}
   */
  const perPageOptions = [
    { value: "5", label: "5 por página" },
    { value: "10", label: "10 por página" },
    { value: "25", label: "25 por página" },
    { value: "50", label: "50 por página" },
  ];

  /**
   * Per page filter state for CustomSelect
   */
  const [perPageSelect, setPerPageSelect] = useState<{
    value: string;
    label: string;
  } | null>({ value: "5", label: "5 por página" });

  /**
   * handles the header clicked and orders the information accordingly
   * @param key is the object key represented by the header (may not be equal)
   * @param asc true if ascending order, descending otherwise
   */
  const handleOrderBy = async (key: string, asc: boolean) => {
    const typedKey = key as keyof ISizeRecord;
    const orderedSizes = sortByKey(foundData, typedKey, asc);
    setFoundData(orderedSizes);
    setClickedHeader({ name: key, asc });
  };

  /**
   * handles the selected filter and filters the current data
   * @param option is the selected filter option
   */
  const handleFilter = (option: ISelectableOption | null) => {
    setClickedHeader({ name: "", asc: false });
    if (!option) {
      setFilteredSizes(sizes);
      setFoundData(sizes);
      return;
    }
    const { value } = option;
    const activeSizes = sizes.filter((size) => size.status === true);
    const inactiveSizes = sizes.filter((size) => size.status === false);
    switch (value) {
      case "Activados":
        setFilteredSizes(activeSizes);
        setFoundData(activeSizes);
        setNoResults(activeSizes.length === 0);
        break;
      case "Desactivados":
        setFilteredSizes(inactiveSizes);
        setFoundData(inactiveSizes);
        setNoResults(inactiveSizes.length === 0);
        break;
      default:
        setFilteredSizes(sizes);
        setFoundData(sizes);
        setNoResults(false);
        break;
    }
  };

  /**
   * handles the filter change and filters the data and returns to the first page
   * @param option is the filter option selected
   */
  const handleFilterChange = (option: ISelectableOption | null) => {
    if (sizes.length !== 0) {
      handleFilter(option);
    }
    setFilter(option);
    setCurrentPage(1);
  };

  /**
   * Handles per page filter change
   * @function handlePerPageChange
   * @param {{ value: string; label: string } | null} newValue - The new per page value
   */
  const handlePerPageChange = (
    newValue: { value: string; label: string } | null
  ) => {
    setPerPageSelect(newValue);
    setPageSize(parseInt(newValue?.value || "5"));
    setCurrentPage(1);
  };

  /**
   * handles the back button click and returns to the previous route
   */
  const onAddSizeClick = () => navigate("/inventories/sizes/add");
  /**
   * handles the search term and returns to the first page
   * @param value is the input provided
   */
  const handleSearch = (value: string) => {
    setClickedHeader({ name: "", asc: false });
    setSearchTerm(value);
    const trimmedValue = value.toLowerCase().trim();
    if (trimmedValue === "") {
      const resetSizes = filter == null || filter.value == "Todos";
      resetSizes ? setFoundData(sizes) : handleFilter(filter);
      setNoResults(false);
      return;
    }
    const search = filteredSizes.filter(
      (size) =>
        size.name.toLowerCase().includes(trimmedValue) ||
        size.label.toLowerCase().includes(trimmedValue)
    );
    setNoResults(search.length === 0);
    setFoundData(search);
    setCurrentPage(1);
  };

  /**
   * handles the page change from a direct click or the other navigation buttons
   * @param pageNumber the page number clicked
   */
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  /**
   * Method that fetch the data of sizes
   */
  const fetchData = async () => {
    setRequestState("loading");
    const { data, statusCode } = await fetchSizes(axiosInstance, {
      per_page: 1000,
      page: 1,
    });
    if (!isSuccessStatusCode(statusCode)) {
      setApiError(true);
      setRequestState("failure");
      return;
    }
    if (!data) return;
    setRequestState("successful");
    const { tallas } = data;
    const entries: ISizeRecord[] = tallas.map((talla) => ({
      id: talla.id,
      name: talla.name,
      label: talla.description,
      status: !!talla.status,
    }));
    setApiError(false);
    setSizes(entries);
    setFilteredSizes(entries);
    setFoundData(entries);
  };

  /**
   * This use effect process the size data and paginates it
   * it also recalculate the total pages by listening the actual filtered data
   * [filteredSizes] is a filtered collection of size data
   * [currentPage] is the actual page clicked from pagination component
   * [pageSize] is the current page size
   */
  useEffect(() => {
    const data = getPaginatedSlice(foundData, currentPage, pageSize);
    const totalPages = Math.max(1, Math.ceil(foundData.length / pageSize));
    setTotalPages(totalPages);
    setPaginatedData(data);
  }, [filteredSizes, foundData, currentPage, pageSize, clickedHeader]);

  /**
   * use effect that fetch the initial data
   */
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <AdminRouteContainer backRoute="/dashboard#inventarios">
      <>
        <Row className="align-items-center mb-4 mt-5">
          <Col className="text-center mt-5">
            <h2 className="fw-bold">Tallas registradas</h2>
          </Col>
        </Row>
        <Row>
          <SearchAndFilterBar
            searchType={"text"}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            filterOptions={filterOptions}
            filterBy={filter}
            isSearchDisabled={false}
            onFilterChange={handleFilterChange}
            customTooltip="Ordena las columnas por nombre o estado"
          />
        </Row>

        {/* Per page selector - aligned below the filter */}
        <Row className="mb-3">
          <Col xs={6}></Col>
          <Col xs={12} className="justify-content-end">
            <div>
              <InputGroup style={{ height: "38px", width: "100%" }}>
                <div
                  className="filter-select-container justify-content-end"
                  style={{
                    height: "100%",
                    minWidth: "0",
                    width: "100%",
                  }}
                >
                  <CustomSelect
                    options={perPageOptions}
                    value={perPageSelect}
                    onChange={handlePerPageChange}
                    placeholder="Elementos por página..."
                    isFilteredIconVisible={false}
                  />
                </div>
              </InputGroup>
            </div>
          </Col>
        </Row>
        <Row style={{ width: "60%", justifySelf: "center" }}>
          <SizesTable
            clickedHeader={clickedHeader}
            orderBy={handleOrderBy}
            requestState={requestState}
            paginatedData={paginatedData}
            noResults={noResults}
            apiError={apiError}
          />
        </Row>
        <Row>
          <Button
            variant="primary"
            type="button"
            onClick={onAddSizeClick}
            className=" fw-bold align-self-center text-center"
          >
            Agregar una talla
          </Button>
        </Row>
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalResults={foundData.length ?? 0}
        />
      </>
    </AdminRouteContainer>
  );
};
